import React, { FC, ReactElement, useContext, useMemo } from 'react'
import { Area, AreaChart, Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { QuickInfoDTO } from '../../../../React/InsightsHub/Types/Types'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { InsightsDashboardLanguage } from '../../../../../i18n'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import { ChartData } from '../../../../NonReact/Types/Types'
import { DASH } from '../../../../React/Common/Constants/Separators'

type Props = {
    quickInfo: Nullable<QuickInfoDTO>
}

const DAY_NAME_MAP: Record<string, string> = {
    Monday: 'Mon',
    Tuesday: 'Tue',
    Wednesday: 'Wed',
    Thursday: 'Thu',
    Friday: 'Fri',
    Saturday: 'Sat',
    Sunday: 'Sun'
}

export const InsightsQuickInfo: FC<Props> = ({ quickInfo }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const busiestDays: ChartData[] = useMemo(
        (): ChartData[] =>
            Object.entries(quickInfo?.BusiestDayStat ?? {})?.map(
                ([name, value]: [string, number]): ChartData => ({
                    name: DAY_NAME_MAP[name],
                    value
                })
            ) ?? [],
        [quickInfo]
    )

    const busiestTimes: ChartData[] = useMemo(
        (): ChartData[] =>
            Object.entries(quickInfo?.BusiestTimeStat ?? {})?.map(([name, value]: [string, number]): ChartData => {
                const endTime: string = name.trim().split(DASH)?.[1]?.trim()
                const endTimeHour: string = endTime?.substring(0, endTime.indexOf(':'))

                return {
                    name: endTimeHour === '00' ? '24' : endTimeHour,
                    value
                }
            }) ?? [],
        [quickInfo]
    )

    return (
        <div className='idis-stat idii-quick-info'>
            {isVarNotEmpty(quickInfo?.BusiestDay) ? (
                <div className='idiiqi-entry'>
                    <div className='idiiqie-title'>
                        <span className='idiiqiet-lowlight'>
                            {language.get(InsightsDashboardLanguage.InsightsDashboard_BusiestDay)} -
                        </span>
                        <span>{quickInfo?.BusiestDay}</span>
                    </div>
                    <div className='idiiqie-subtitle'>
                        {language.get(InsightsDashboardLanguage.InsightsDashboard_OfLast30Days)}
                    </div>

                    <ResponsiveContainer width='100%' height={250}>
                        <BarChart className='idiiqie-chart idiiqie-chart-days' data={busiestDays}>
                            <XAxis dataKey='name' />
                            <YAxis />
                            <Tooltip
                                wrapperClassName='idiiqie-tooltip'
                                separator={EMPTY_STRING}
                                labelFormatter={(label: string): string =>
                                    language.getInterpolated(
                                        InsightsDashboardLanguage.InsightsDashboard_Tooltip_DayLabel,
                                        label
                                    )
                                }
                                formatter={(value: string): [string, string] => [
                                    language.getInterpolated(
                                        InsightsDashboardLanguage.InsightsDashboard_Tooltip_CallsLabel,
                                        value
                                    ),
                                    EMPTY_STRING
                                ]}
                            />
                            <Bar dataKey='value' fill='currentColor' />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <></>
            )}
            {isVarNotEmpty(quickInfo?.BusiestTime) ? (
                <div className='idiiqi-entry'>
                    <div className='idiiqie-title'>
                        <span className='idiiqiet-lowlight'>
                            {language.get(InsightsDashboardLanguage.InsightsDashboard_PeakCalls)} -
                        </span>
                        <span>{quickInfo?.BusiestTime}</span>
                    </div>
                    <div className='idiiqie-subtitle'>
                        {language.get(InsightsDashboardLanguage.InsightsDashboard_OverLast30Days)}
                    </div>
                    <ResponsiveContainer width='100%' height={250}>
                        <AreaChart className='idiiqie-chart idiiqie-chart-times' data={busiestTimes}>
                            <XAxis ticks={[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]} interval={0} />
                            <YAxis />
                            <Tooltip
                                wrapperClassName='idiiqie-tooltip'
                                separator={EMPTY_STRING}
                                labelFormatter={(label: string): string =>
                                    language.getInterpolated(
                                        InsightsDashboardLanguage.InsightsDashboard_Tooltip_TimeLabel,
                                        label
                                    )
                                }
                                formatter={(value: string): [string, string] => [
                                    language.getInterpolated(
                                        InsightsDashboardLanguage.InsightsDashboard_Tooltip_CallsLabel,
                                        value
                                    ),
                                    EMPTY_STRING
                                ]}
                            />
                            <Area
                                type='linear'
                                dataKey='value'
                                fill='currentColor'
                                stroke='currentColor'
                                strokeWidth={2}
                                dot={{ fill: 'white', fillOpacity: 1, stroke: 'currentColor' }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <></>
            )}
        </div>
    )
}
